// Presets a building starts with, since features are unreachable without them (incident sign-off
// needs an Executive Director post). Stable keys keep seeding idempotent and non-overwriting: a
// renamed role keeps its wording.

export const FACILITY_TEAM_ROLE_PRESETS = [
  { key: "executive_director", name: "Executive Director" },
  { key: "administrator", name: "Administrator" },
  { key: "rn_consultant", name: "RN Consultant" },
  { key: "licensed_vocational_nurse", name: "Licensed Vocational Nurse (LVN)" },
  { key: "resident_care_coordinator", name: "Resident Care Coordinator" },
  { key: "medication_technician", name: "Medication Technician" },
  { key: "caregiver", name: "Caregiver" },
  { key: "activities_director", name: "Activities Director" },
  { key: "dietary_supervisor", name: "Dietary Supervisor" },
  { key: "maintenance_supervisor", name: "Maintenance Supervisor" },
] as const;

export const INCIDENT_CATEGORY_PRESETS = [
  { key: "fall", name: "Fall" },
  { key: "medication_error", name: "Medication Error" },
  { key: "behavioral", name: "Behavioral" },
  { key: "medical_emergency", name: "Medical Emergency" },
  { key: "injury", name: "Injury" },
  { key: "elopement", name: "Elopement" },
  { key: "property_damage", name: "Property Damage" },
  { key: "other", name: "Other" },
] as const;

/** The three resident-profile pick lists, seeded because two buildings spelling "Self by cheque"
 * differently make a report that cannot add them. */
export const DEMOGRAPHIC_OPTION_PRESETS = [
  { category: "payment_mode", key: "self_by_check", name: "Self By Check" },
  { category: "payment_mode", key: "family_by_check", name: "Family By Check" },
  { category: "payment_mode", key: "direct_express_withdrawal", name: "Direct Express Withdrawal" },
  { category: "payment_mode", key: "bank_card_withdrawal", name: "Bank Card Withdrawal" },
  { category: "payment_mode", key: "ach_auto", name: "ACH Auto" },
  { category: "payment_mode", key: "ach_manual", name: "ACH Manual" },
  { category: "payment_mode", key: "trust_fund", name: "Trust Fund" },
  { category: "payment_mode", key: "cash", name: "Cash" },
  { category: "showers", key: "self", name: "Self" },
  { category: "showers", key: "light_assistance", name: "Light Assistance" },
  { category: "showers", key: "dependent", name: "Dependent" },
  { category: "room_cleaning", key: "self", name: "Self" },
  { category: "room_cleaning", key: "by_facility", name: "By Facility" },
] as const;

// The CRM's own pick lists. Without them the lead form dropdowns are empty and a lead cannot be
// closed (closing asks why). Industry defaults, not a fixed vocabulary: operators rename or switch
// them off, and the stable key keeps that.
export const LEAD_SOURCE_TYPE_PRESETS = [
  { key: "online_digital", name: "Online / Digital" },
  { key: "professional_referral", name: "Professional Referral" },
  { key: "family_friends", name: "Family & Friends" },
  { key: "community_events", name: "Community & Events" },
  { key: "advertising", name: "Advertising" },
  { key: "walk_in_internal", name: "Walk-in / Internal" },
  { key: "other", name: "Other" },
] as const;

export const LEAD_CONTACT_METHOD_PRESETS = [
  { key: "phone", name: "Phone Call" },
  { key: "email", name: "Email" },
  { key: "web_form", name: "Web Form" },
  { key: "walk_in", name: "Walk-in" },
  { key: "referral_fax", name: "Referral / Fax" },
  { key: "text_sms", name: "Text / SMS" },
  { key: "social_media", name: "Social Media" },
  { key: "other", name: "Other" },
] as const;

export const LEAD_LOST_REASON_PRESETS = [
  { key: "chose_another_community", name: "Chose Another Community" },
  { key: "cost_affordability", name: "Cost / Affordability" },
  { key: "higher_level_of_care", name: "Needed a Higher Level of Care" },
  { key: "stayed_home", name: "Stayed at Home / Home Health" },
  { key: "no_availability", name: "No Availability / Wrong Room Type" },
  { key: "location", name: "Location" },
  { key: "family_decision", name: "Family Decision / Timing Changed" },
  { key: "medically_declined", name: "Declined, Care Needs We Cannot Meet" },
  { key: "deceased", name: "Resident Passed Away" },
  { key: "unresponsive", name: "Went Unresponsive" },
  { key: "other", name: "Other" },
] as const;

export const LEAD_WON_REASON_PRESETS = [
  { key: "tour_experience", name: "Tour Experience" },
  { key: "price_value", name: "Price / Value" },
  { key: "location", name: "Location" },
  { key: "professional_referral", name: "Professional Referral" },
  { key: "family_word_of_mouth", name: "Family / Word of Mouth" },
  { key: "online_listing", name: "Online Listing or Website" },
  { key: "advertising_campaign", name: "Advertising Campaign" },
  { key: "community_event", name: "Community Event" },
  { key: "availability_timing", name: "Availability / Timing" },
  { key: "care_services", name: "Care & Services Offered" },
  { key: "other", name: "Other" },
] as const;

/** Starter referral sources by source type. Operators add rows constantly, so these seed only a
 * building with none (seeding by name would put "Walk-in" beside an existing "Walk-In"). */
export const LEAD_SOURCE_PRESETS = [
  { typeKey: "online_digital", name: "Website Inquiry" },
  { typeKey: "online_digital", name: "A Place for Mom" },
  { typeKey: "online_digital", name: "Caring.com" },
  { typeKey: "online_digital", name: "SeniorAdvisor" },
  { typeKey: "professional_referral", name: "Professional Referral" },
  { typeKey: "professional_referral", name: "Physician Referral" },
  { typeKey: "professional_referral", name: "Hospital Discharge" },
  { typeKey: "professional_referral", name: "Skilled Nursing Facility" },
  { typeKey: "professional_referral", name: "UHC Service Coordinator Referral" },
  { typeKey: "professional_referral", name: "Molina Service Coordinator Referral" },
  { typeKey: "family_friends", name: "Family Referral" },
  { typeKey: "family_friends", name: "Friend Referral" },
  { typeKey: "community_events", name: "Community Event" },
  { typeKey: "community_events", name: "Health Fair" },
  { typeKey: "advertising", name: "Print Ad" },
  { typeKey: "advertising", name: "Direct Mail" },
  { typeKey: "walk_in_internal", name: "Walk-in" },
  { typeKey: "walk_in_internal", name: "Internal Transfer" },
  { typeKey: "other", name: "Other" },
] as const;

/** Starter labels for residents, leads, staff and tasks, so the tags screen is not empty on day
 *  one. They carry no key, so they go only into a building that has none; it edits them freely. */
export const TAG_PRESETS = [
  {
    name: "Fall risk",
    color: "#EF4444",
    description: "History of falls or unsteady on their feet.",
  },
  {
    name: "Memory care",
    color: "#8B5CF6",
    description: "Dementia or memory loss; cueing and redirection.",
  },
  {
    name: "Elopement risk",
    color: "#DC2626",
    description: "May wander out; door alerts and check-ins.",
  },
  {
    name: "Hospice",
    color: "#6B7280",
    description: "On hospice; comfort care and the hospice team on file.",
  },
  { name: "Diabetic", color: "#F59E0B", description: "Diabetic diet and glucose checks." },
  {
    name: "Dietary restriction",
    color: "#F97316",
    description: "Allergies or a special diet; check before serving.",
  },
  { name: "Respite", color: "#06B6D4", description: "A short stay, not a permanent move-in." },
  { name: "Veteran", color: "#1D4ED8", description: "May qualify for VA Aid and Attendance." },
  { name: "Medicaid", color: "#10B981", description: "On Medicaid, or applying for it." },
  { name: "Hot lead", color: "#E11D48", description: "Ready to decide; follow up today." },
  { name: "Tour scheduled", color: "#3B82F6", description: "Has a tour on the calendar." },
  { name: "Waitlist", color: "#A855F7", description: "Waiting for a room to open." },
  {
    name: "New hire",
    color: "#14B8A6",
    description: "In their first 90 days; orientation and check-ins.",
  },
  { name: "Med tech certified", color: "#0EA5E9", description: "Cleared to pass medications." },
  { name: "Urgent", color: "#B91C1C", description: "Needs doing today." },
  { name: "Regulatory", color: "#7C3AED", description: "State licensing and survey readiness." },
] as const;

/** The kinds of document every building files. Product-wide: unique among the standard types. */
export const DOCUMENT_TYPE_PRESETS = [
  { name: "Photo ID", isMedicalRecord: false, includeInFaceSheet: true },
  { name: "Insurance card", isMedicalRecord: false, includeInFaceSheet: true },
  { name: "Medicare or Medicaid card", isMedicalRecord: false, includeInFaceSheet: true },
  { name: "Power of attorney", isMedicalRecord: false, includeInFaceSheet: true },
  { name: "Advance directive", isMedicalRecord: true, includeInFaceSheet: true },
  { name: "Physician's statement", isMedicalRecord: true, includeInFaceSheet: false },
  { name: "Physician's orders", isMedicalRecord: true, includeInFaceSheet: false },
  { name: "TB test result", isMedicalRecord: true, includeInFaceSheet: false },
  { name: "Immunization record", isMedicalRecord: true, includeInFaceSheet: false },
  { name: "Medication list", isMedicalRecord: true, includeInFaceSheet: false },
  { name: "Care plan", isMedicalRecord: true, includeInFaceSheet: false },
  { name: "Assessment", isMedicalRecord: true, includeInFaceSheet: false },
  { name: "Residency agreement", isMedicalRecord: false, includeInFaceSheet: false },
  { name: "Financial agreement", isMedicalRecord: false, includeInFaceSheet: false },
  { name: "Move-in inventory", isMedicalRecord: false, includeInFaceSheet: false },
  { name: "Incident report", isMedicalRecord: true, includeInFaceSheet: false },
  { name: "Discharge summary", isMedicalRecord: true, includeInFaceSheet: false },
  { name: "Other", isMedicalRecord: false, includeInFaceSheet: false },
] as const;

/** The walking frames, hoists and aids a chart can name. Product-wide, keyed by code. */
export const ASSISTIVE_DEVICE_PRESETS = [
  { code: "walker", label: "Walker" },
  { code: "rollator", label: "Rollator" },
  { code: "cane", label: "Cane" },
  { code: "wheelchair", label: "Wheelchair" },
  { code: "power_wheelchair", label: "Power wheelchair" },
  { code: "scooter", label: "Mobility scooter" },
  { code: "gait_belt", label: "Gait belt" },
  { code: "mechanical_lift", label: "Mechanical lift" },
  { code: "hospital_bed", label: "Hospital bed" },
  { code: "bedside_commode", label: "Bedside commode" },
  { code: "shower_chair", label: "Shower chair" },
  { code: "hearing_aids", label: "Hearing aids" },
  { code: "glasses", label: "Glasses" },
  { code: "dentures", label: "Dentures" },
  { code: "oxygen", label: "Oxygen" },
  { code: "cpap", label: "CPAP" },
  { code: "prosthesis", label: "Prosthesis" },
] as const;

/** Starter checklists: the tasks around a move-in, a monthly review and a move-out. */
export const CHECKLIST_TEMPLATE_PRESETS = [
  {
    name: "Move-in",
    description: "Everything that must be on file or done for a new resident.",
    items: [
      { title: "Residency agreement signed", category: "legal" },
      { title: "Photo ID and insurance cards on file", category: "admission" },
      { title: "Physician's statement received", category: "medical" },
      { title: "TB test result on file", category: "medical" },
      { title: "Medication list reconciled with the pharmacy", category: "medical" },
      { title: "Care plan completed", category: "medical" },
      { title: "Power of attorney and advance directive on file", category: "legal" },
      { title: "Emergency contacts recorded", category: "admission" },
      { title: "Deposit and first month collected", category: "financial" },
      { title: "Room inventory done and keys issued", category: "admission" },
      { title: "Resident rights and house rules reviewed", category: "legal" },
      { title: "Photo taken for the face sheet", category: "admission" },
    ],
  },
  {
    name: "Monthly review",
    description: "Done for every resident each month.",
    items: [
      { title: "Weight and vitals recorded", category: "medical" },
      { title: "Medication list reviewed", category: "medical" },
      { title: "Skin check completed", category: "medical" },
      { title: "Care plan changes noted", category: "medical" },
      { title: "Family update call made", category: "other" },
      { title: "Monthly statement sent", category: "financial" },
    ],
  },
  {
    name: "Move-out",
    description: "Closing a resident's stay.",
    items: [
      { title: "Notice on file", category: "legal" },
      { title: "Final medication count and transfer", category: "medical" },
      { title: "Records sent to the receiving provider", category: "medical" },
      { title: "Room inventory checked", category: "other" },
      { title: "Deposit refund processed", category: "financial" },
      { title: "Family portal access closed", category: "other" },
    ],
  },
] as const;

/** Where an assisted-living operator's money goes. Per organization, seeded into one with none. */
export const EXPENSE_CATEGORY_PRESETS = [
  { name: "Payroll and benefits", categoryType: "payroll" },
  { name: "Food and dietary", categoryType: "operating" },
  { name: "Medical supplies", categoryType: "operating" },
  { name: "Housekeeping and laundry", categoryType: "operating" },
  { name: "Activities", categoryType: "operating" },
  { name: "Maintenance and repairs", categoryType: "operating" },
  { name: "Transportation", categoryType: "operating" },
  { name: "Marketing", categoryType: "operating" },
  { name: "Utilities", categoryType: "overhead" },
  { name: "Rent or mortgage", categoryType: "overhead" },
  { name: "Insurance", categoryType: "overhead" },
  { name: "Licensing and fees", categoryType: "overhead" },
  { name: "Professional services", categoryType: "overhead" },
  { name: "Office and software", categoryType: "overhead" },
  { name: "Capital improvements", categoryType: "capital" },
  { name: "Taxes", categoryType: "tax_efficiency" },
] as const;

/** How long a building lets a web session sit idle, in minutes. */
export const IDLE_TIMEOUT_MINUTES = { min: 15, max: 480, default: 120 } as const;
